import axios from 'axios';
import merge from 'deepmerge';
import EventEmitter from 'eventemitter3';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';

import FeatureProvider from '@exp/features/FeatureProvider';

import analytics from 'exp-analytics-collector';
import SignOnEmitter from 'core-front-end/js/core/analytics/signon-emitter';

import { InitialFormModes, Layout } from './constants/constants';
import Events from './constants/events';
import Modes from './constants/modes';
import ContainerView from './container/Container';
import PendoListener from './listeners/PendoListener';
import { getOnboardingConfig } from './api/sign-on';
import { selectContext, selectDestination, setConfig, setContext } from './redux/context';
import { selectHistory } from './redux/history';
import publisher from './redux/publisher';
import createStore from './redux/store';
import Log from './utilities/Log';
import { InstanceContext } from './utilities/utilities';

import './exp-onboarding.less';

const OnboardingFlow = React.lazy(
  () =>
    import(
      /* webpackChunkName: "exp-onboarding.bundle.onboarding-flow" */
      './pages/OnboardingFlow'
    ),
);
OnboardingFlow.displayName = 'OnboardingFlow';

const APP_NAME = 'exp-onboarding';
const MAX_CONFIG_ATTEMPTS = 3;

/**
 * Factory for creating an Onboarding Service
 *
 * TODO
 * - Fix unit tests
 * - Convert Google Maps to more modern React versions
 * - Code splitting + Lazy pages/flows
 * - Move all the things to the shadow DOM
 * - Deprecate/remove ExpCategorySelector & ExpManageGroups
 *
 * @param customProperties Object
 * @returns {Object} Onboarding Service
 */
class BaseOnboarding extends EventEmitter {
  #configPromise = null;

  // Initialize the default config options
  #configurations = {
    // Base config options
    base: {
      allowAuthenticated: false,
      expectAuthenticated: false,
      logLevel: 1, // Default to info level logging
      mode: 'onboarding',
      parent: 'body',
      theme: 'v1', // Default to a v1 theme

      // The container is being closed
      // close: () => {},

      // The criteria for completion has been met
      // complete: () => {},

      // Default the isComplete handler to be done if the user is authenticated
      isComplete: ({ context, config }) => config?.isAuthenticated || context?.user?.userId,
    },

    // Options configured through the .configure() method
    override: {},
  };

  #logger = null;

  #pages = [];

  #signOnEmitter; // TODO is this necessary to store as a field?

  // Look for url parameters prefixed with 'onb_'
  #urlPrefix = [];

  #instances = [];

  // Extend the event framework to have 'singleton' events that will automatically invoke with newly added listeners
  #publishedEvents = {};

  #singletonEvents = ['mount'];

  constructor({ pages = undefined, singletonEvents = [], urlParamPrefix, ...options } = {}) {
    super();

    // Expose the analytics collector, to allow better testing and event tracking on all pages
    this.analytics = analytics;

    this.#pages = pages || [OnboardingFlow];
    this.#singletonEvents = singletonEvents || [];

    // Wire in some optional base configurations
    this.#configurations.base = merge.all([this.#configurations.base, options]);

    if (urlParamPrefix && !this.#urlPrefix.includes(urlParamPrefix)) {
      this.#urlPrefix.push(urlParamPrefix);
    }

    // Configure axios to send the credentials along with any request
    axios.defaults.withCredentials = true;

    // Hook into the 'will-send' event on the analytics collector and bubble those out
    this.analytics.on('will-send', (event) => {
      this.emit(Events.AnalyticEvent, event.action, event.data, event);
    });

    // Emit a 'mount' event letting everyone know we're ready
    this.emit(Events.Mount, this);

    // Invoke the onLoaded method when the config is loaded
    this.on(Events.Loaded, this.onLoaded);

    const computedConfig = this.#computeConfig();

    // setup the logger for this instance based on the computed log level from the config
    this.#logger = new Log(computedConfig.computed.logLevel);

    // Determine if we should auto-show
    if (computedConfig.computed.autoShow) {
      const autoShow = () => {
        if (!this.#instances.some((instance) => instance.layout === Layout.MODAL)) {
          this.show();
        }
      };

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => autoShow());
      } else {
        autoShow();
      }
    }
  }

  /**
   * Persist a set of configurations
   *
   * @param options Configuration object or string key
   * @param value Configuration value or configuration bucket name
   * @param bucket Configuration bucket name
   *
   * @example configure('foo', 'bar');
   * @example configure('foo', 'bar', 'overrides');
   * @example configure({ foo: 'bar' });
   * @example configure({ foo: 'bar' }, 'overrides');
   */
  configure(options, value, bucket = 'override') {
    if (typeof options === 'object') {
      const resolvedBucket = this.#pickConfigBucket(value, bucket);
      this.#configurations[resolvedBucket] = merge(this.#configurations[resolvedBucket] || {}, options || {});
    } else if (typeof options === 'string') {
      const resolvedBucket = this.#pickConfigBucket(bucket);
      this.#configurations[resolvedBucket] = merge(this.#configurations[resolvedBucket] || {}, {
        [options]: value,
      });
    }
  }

  /**
   * Emit an event
   *
   * @param event The event name
   * @param args The event
   * @returns {boolean}
   */
  emit(event, ...args) {
    // Cache off the event data if the event is registered as a 'singleton' event
    if (this.#singletonEvents.includes(event)) {
      this.#publishedEvents[event] = args;
    }

    return super.emit(event, ...args);
  }

  /**
   * Load the config for onboarding
   *
   * @param options Inline configuration options
   * @return {Promise}
   */
  getConfig(options = {}) {
    const { promise } = this.#loadConfig(options);
    return promise;
  }

  /**
   * Get the store for the current instance
   *
   * @param id The instance ID
   * @returns {*}
   */
  getStore(id) {
    if (id) {
      return this.#instances.find((instance) => instance.id === id)?.store;
    }

    const [instance] = this.#instances;
    return instance?.store;
  }

  /**
   * Hide all modal instances or the specified one
   *
   * @param {string} [id] The instance ID — omit to hide all modal instances
   */
  hide(id) {
    this.#instances = this.#instances
      .map((instance) => {
        // Unmount all existing modals
        if ((id && instance.id === id) || (!id && instance.layout === Layout.MODAL)) {
          instance.root?.unmount(); // Unmount the react dom root

          const el = document.getElementById(instance.id);
          if (el) el.remove(); // Remove the container element

          return null;
        }

        return instance;
      })
      .filter((instance) => instance);
  }

  /**
   * Listen for events of a given name
   * @param event The event name
   * @param fn The listener function
   * @param context The event context object
   * @returns {EventEmitter}
   */
  on(event, fn, context) {
    // Invoke the callback if the event is a 'singleton' event has already been fired
    const lastEvent = this.#publishedEvents[event];
    if (this.#singletonEvents.includes(event) && lastEvent) {
      window.setTimeout(() => {
        fn(...lastEvent, context);
      });
    }

    return super.on(event, fn, context);
  }

  /**
   * Listen for events of a given name (once)
   * @param event The event name
   * @param fn The listener function
   * @param context The event context object
   * @returns {EventEmitter}
   */
  once(event, fn, context) {
    // Invoke the callback if the event is a 'singleton' event has already been fired
    const lastEvent = this.#publishedEvents[event];
    if (this.#singletonEvents.includes(event) && lastEvent) {
      window.setTimeout(() => {
        fn(...lastEvent, context);
      });
    }

    return super.once(event, fn, context);
  }

  /**
   * Triggered automatically when the config is loaded
   * @param config Parsed Onboarding Config
   */
  onLoaded(config) {
    if (config.toggles?.pendo !== false && !this.pendoListener) {
      // Wire in the pendo listener if it is enabled
      this.pendoListener = new PendoListener(this);
    }
  }

  /**
   * Create and show an instance of onboarding
   */
  show(options = {}) {
    // Initialize the config for this onboarding instance
    const configs = this.#loadConfig(options);
    const { computed } = configs;

    // Get a handle on the parent element
    const target = computed.containerElementId ? `#${computed.containerElementId}` : computed.parent;
    const parent = document.querySelector(target);
    if (!parent) {
      throw new Error(`Error initializing onboarding instance. Element with selector="${target}" does not exist.`);
    }

    // Inject an element into the DOM to act as our root
    const id = `onb-${Math.round(Math.random() * 100000) * Date.now()}`;
    const container = document.createElement('div');
    container.id = id;
    parent.append(container);

    // Create the root that the instance will live within
    const root = ReactDOM.createRoot(container, { identifierPrefix: id });

    // Hide the other modal instances (if there are any)
    if (computed.layout === Layout.MODAL) {
      this.hide();
    }

    const instance = {
      id,
      layout: computed.layout,
      root,
    };
    this.#instances.unshift(instance);

    // Wait for the config to be loaded before continuing
    configs.promise
      .then((config) => {
        if (instance.layout === Layout.MODAL && !this.#instances.includes(instance)) {
          // Bail if modal layout and the root is no longer current (double-show before config loaded)
          return;
        }

        // Configure the axios baseURL
        axios.defaults.baseURL = config.basePath;

        // Configure the webpack public path to pull everything from the CDN
        if (config.uris.cdn) {
          // eslint-disable-next-line camelcase,no-undef
          __webpack_public_path__ = `${config.uris.cdn}${process.env.PUBLIC_URL}`;
        }

        // Initialize the redux store with the full config
        const store = createStore({
          config,
          middleware: [
            // Event publisher middleware to emit events that consumers can hook into
            publisher({
              onApplicationSubmit: (data) => this.emit(Events.ApplicationSubmit, data),
              onQuestionFocus: (data) => this.emit(Events.QuestionFocus, data.index, data.question),
            }),
          ],
        });

        // Give the instance a handle on the store
        instance.store = store;
        // Do some checks if the user is authenticated
        if (config.isAuthenticated) {
          store.dispatch(setContext({ initialFormMode: InitialFormModes.PRE_AUTHENTICATED }));

          // Check that we are allowing an authenticated user
          if (!config.allowAuthenticated && !config.expectAuthenticated) {
            // An authenticated user is not expected. Trigger the close handler.
            this.#close(instance, config);
            return;
          }

          // Set in the ifAuthenticated config if specified
          const ifAuthenticated =
            typeof config.ifAuthenticated === 'function'
              ? config.ifAuthenticated(selectContext(store), config)
              : config.ifAuthenticated;
          if (ifAuthenticated) {
            store.dispatch(setConfig(config.ifAuthenticated));
          }
        }
        let resolvedConfig = config;
        if (config.mode === Modes.repReferral.name && config.isAuthenticated) {
          resolvedConfig = { ...config, theme: 'v1' };
        } else if ([Modes.repReferral.name, Modes.referral.name, Modes.onboarding.name].includes(config.mode)) {
          resolvedConfig = {
            ...config,
            toggles: {
              ...config.toggles,
              categorySelector: false,
              isolateNameFields: true,
              isReferral: config.mode === Modes.repReferral.name || config.mode === Modes.referral.name,
            },
            mode: 'takeover',
            signUpDestination: '/user/setup',
            destination: '/user/setup',
            theme: 'v3',
          };
        }
        // Create the root that the instance will be mounted into
        root.render(
          <InstanceContext.Provider value={this}>
            <Provider store={store}>
              <FeatureProvider appName={`onboarding - ${computed.mode}`}>
                <ContainerView
                  analytics={this.analytics}
                  close={(e) => this.#close(instance, resolvedConfig, e)}
                  config={resolvedConfig}
                  layout={instance.layout}
                  onPageChange={(page) => {
                    // Bubble out page change events
                    const history = selectHistory(store.getState());
                    this.emit(Events.PageChange, {
                      current: page.name,
                      history: (history || []).map((p) => p.name),
                    });
                  }}
                  pages={this.#pages}
                  parent={config.containerElementId ? `#${config.containerElementId}` : config.parent}
                  theme={resolvedConfig.theme}
                />
              </FeatureProvider>
            </Provider>
          </InstanceContext.Provider>,
        );
        this.emit(Events.Show, {
          baseConfig: configs.base,
          baseConfigOverrides: configs.override,
          inlineConfig: configs.inline,
          layout: computed.layout,
          mode: computed.mode,
          modeConfig: configs.mode,
          theme: computed.theme,
          urlConfig: configs.url,
        });
      })
      .catch((error) => {
        this.#logger.error('Error loading config', error);
        if (this.#instances?.includes(instance)) {
          this.hide(id);
        } else {
          root.unmount?.();
          container.remove();
        }
      });

    return instance;
  }

  #close(instance, config, e = {}) {
    let { url: destination } = e || {};

    const state = instance.store.getState();
    const context = selectContext(state);
    const visitedPages = selectHistory(state)
      ?.map((p) => p.name)
      .reverse();

    // Check if the session is considered "complete" according to the configured isComplete method
    const isComplete = !!config.isComplete?.({ context, config });

    // Use the configured destination if we are considered complete
    if (!destination && isComplete) {
      destination = selectDestination(state);
    }

    // Sanitize the destination using the configured cleanser
    if (destination && typeof config.sanitizeDestination === 'function') {
      destination = config.sanitizeDestination(destination, { config, context });
    }

    // Identify if there is a complete handler
    const completeHandler =
      isComplete && !config.blockCustomComplete && typeof config.complete === 'function' ? config.complete : null;

    if (isComplete) {
      // Emit a complete event with the context
      this.emit(Events.Complete, { config, context, history: visitedPages });

      // Invoke the configured complete callback
      if (completeHandler) {
        completeHandler({ config, context, history: visitedPages, url: destination });
      }
    }

    // Invoke the configured close callback
    config.close?.({ config, context });
    this.emit(Events.Close, { complete: isComplete, config, context, history: visitedPages });

    // Unmount the current instance if it's still the active instance
    this.hide(instance.id);

    // Redirect the user to the specified URL even if we're not complete
    if (destination) window.location.href = destination;
  }

  #computeConfig(options = {}) {
    const inline = options || {};

    // Merge together the base & configured overrides
    const configured = merge.all([this.#configurations.base, this.#configurations.override]);
    // Parse the URL query parameters for any configuration options
    const excludedUrlParams = ['analytics', 'basePath', 'facebook', 'messages', 'uris']
      .concat(configured.excludedUrlParams || [])
      .concat(inline.excludedUrlParams || []);

    const queryParams = new URLSearchParams(window.location.search);
    const urlConfig = Array.from(queryParams.entries())
      .filter(([key, value]) => value && this.#urlPrefix.some((prefix) => key?.startsWith(prefix)))
      .map(([k, value]) => {
        // Strip off the prefix from the key
        const key = this.#urlPrefix.reduce(
          (memo, prefix) => (memo.startsWith(prefix) ? memo.substring(prefix.length) : memo),
          k,
        );
        return [key, value];
      })
      .filter(([key]) => !excludedUrlParams.some((p) => key.startsWith(p)))
      .reduce((map, [key, v]) => {
        // Parse the value
        let value = v;

        // Parse array-ish values (wrapped in [] brackets or comma-separated)
        if ((value.startsWith('[') && value.endsWith(']')) || value.includes(',')) {
          // Unwrap [] from array parameters
          if (value.startsWith('[') && value.endsWith(']')) {
            value = value.substring(1, value.length - 1);
          }

          // Split on commas and convert numeric values to numbers
          value = value.split(',').map((p) => (/^\d+$/.test(p) ? +p : p));
        }

        // Convert true/false to a boolean
        const lower = v.toLowerCase();
        if (lower === 'true' || lower === 'false') {
          value = lower === 'true';
        }

        // Convert numeric values to numbers
        if (/^\d+$/.test(v)) {
          value = +value;
        }

        return { ...map, [key]: value };
      }, {});

    // Get the mode config from the configured mode
    const mode = inline.mode || urlConfig.mode || configured.mode || null;
    const modeConfig = (typeof Modes[mode] === 'function' ? Modes[mode]() : Modes[mode]) || {};

    // Determine the actual config minus the loaded values
    const computed = merge.all([configured, urlConfig, modeConfig, inline]);
    computed.mode = mode;
    switch (mode) {
      case Modes.qualify.name:
        computed.layout = Layout.QUALIFY;
        break;
      case Modes.manageTeams.name:
        computed.layout = Layout.MANAGE;
        break;
      case Modes.setup.name:
        computed.layout = Layout.SETUP;
        break;
      case Modes.vets.name:
        computed.layout = Layout.VETS;
        break;
      case Modes.postAffiliation.name:
        computed.layout = Layout.POST_AFFILIATION;
        break;
      case Modes.interests.name:
        computed.layout = Layout.INTEREST;
        break;
      default:
        computed.layout = computed.containerElementId ? Layout.STATIC : Layout.MODAL;
    }
    // Sanitize the computed config values
    computed.suggestedGroups = computed.suggestedGroups ? [computed.suggestedGroups].flat() : undefined;
    // A single `?onb_landingTeamClassIds=8` arrives as a scalar, so flatten it the same way.
    computed.landingTeamClassIds = computed.landingTeamClassIds ? [computed.landingTeamClassIds].flat() : undefined;
    computed.ensureTeam = !!(
      computed.ensureTeam &&
      (computed.teamId || computed.teamKey || computed.suggestedGroups?.length)
    );

    return {
      base: this.#configurations.base,
      computed,
      configured,
      inline,
      mode: modeConfig,
      override: this.#configurations.override,
      url: urlConfig,
    };
  }

  #loadConfig(options = {}) {
    // Compute the non-loading configs
    const configs = this.#computeConfig(options);

    // Load the config using the configured IP and basePath
    const { basePath, ip } = configs.computed;
    axios.defaults.baseURL = basePath;

    if (options.staleConfig || !this.#configPromise || this.#configPromise.ip !== ip) {
      this.#configPromise = new Promise((resolve, reject) => {
        const fetchConfig = (attempt = 1) => {
          getOnboardingConfig(ip ? { ip } : undefined)
            .then((data) => {
              // Initialize the analytics collector
              if (data.analytics) {
                this.analytics.init({
                  appName: APP_NAME,
                  gaId: data.analytics.gaId,
                  url: data.analytics.collectorUrl,
                });

                if (!this.#signOnEmitter) {
                  // TODO this seems smelly
                  this.#signOnEmitter = new SignOnEmitter(this.analytics, { uuid: '' });
                }
              }
              resolve(data);
            })
            .catch((error) => {
              if (attempt <= MAX_CONFIG_ATTEMPTS) {
                this.#logger.warn(`Error loading config, trying again; attempt=${attempt};`, error);
                fetchConfig(attempt + 1);
              } else {
                reject(error);
              }
            });
        };

        fetchConfig();
      });

      // Set the IP onto the promise so we can reload if it changes
      this.#configPromise.ip = ip;
    }

    // Overload the config promise with all the other bits
    return {
      ...configs,
      promise: this.#configPromise.then((loaded) => {
        const merged = merge.all([
          {
            inlineConfig: configs.inline, // Include the inline config
          },
          loaded,
          configs.computed,
        ]);
        // Publish the messages out onto the Exp.localeContext
        window.Exp = window.Exp || {};
        window.Exp.localeContext = merge(window.Exp.localeContext || {}, merged.messages || {});
        // Flatten the toggles into an object
        const toggles = Object.entries(merged)
          .filter(([key]) => key.startsWith('toggles.'))
          .reduce((map, [key, value]) => ({ ...map, [key.substring(8)]: value }), {
            ...(merged.toggles || {}),
          });

        // Flatten the config with the 'toggles.X' excluded in favor of the object syntax
        const flattened = Object.entries(merged)
          .filter(([key]) => key !== 'toggles' && !key.startsWith('toggles.'))
          .reduce((map, [key, value]) => ({ ...map, [key]: value }), { toggles });

        // Run the configured parse method to allow the config to clean itself up
        const parsed = configs.computed.parse?.(flattened) || flattened;

        // Emit a loaded event with the merged config
        this.emit(Events.Loaded, parsed);

        // Resolve out the config object to be used for the onboarding instance
        return parsed;
      }),
    };
  }

  // Utility method for determining which bucket the config options should go in
  #pickConfigBucket = (...args) => {
    const options = Object.keys(this.#configurations);
    return args.find((bucket) => options.includes(bucket)) || 'override';
  };
}

export default BaseOnboarding;
